import React from 'react';

import { TABLET_COLUMNS } from './constants';

/**
 * Turning a table row into a mobile card.
 *
 * The card used to be the table row transposed: every column, in order, as a
 * stacked label/value pair. That made one job 315px tall on a 390px phone —
 * two and a half records per screen — with "External Job: No" given the same
 * weight as the title, and empty columns rendering as a labelled "-".
 *
 * A card has a hierarchy instead: what it is, what state it is in, and a few
 * supporting facts. This module decides which column plays which part, so the
 * decision is one testable function rather than something each list page
 * reimplements.
 */

/** A column, with its label and already-rendered value. */
export interface CardField {
  selector: string;
  label: React.ReactNode;
  value: React.ReactNode;
}

export interface CardLayout {
  title: React.ReactNode;
  /** State-ish column (Status, Stage), promoted into the header. */
  status: CardField | null;
  /** The most identifying remaining column, shown unlabelled under the title. */
  subtitle: CardField | null;
  /** Everything else, as labelled pairs. */
  facts: CardField[];
  /**
   * Row actions (buttons, icon buttons), rendered unlabelled in the card's footer. Only a page
   * that passes `columns` gets any: the heuristic cannot tell an action cell from a value.
   */
  actions: CardField[];
}

/**
 * The part a column plays on the phone card.
 *
 * `hidden` keeps the column in the table but off the card: a facts list is for reading, and some
 * columns (an internal id, a flag that repeats the status) are noise at 390px.
 */
export type CardRole = 'title' | 'status' | 'subtitle' | 'fact' | 'action' | 'hidden';

/** What a page may say about one column. Everything is optional; unsaid means "decide for me". */
export interface ColumnConfig {
  card?: CardRole;
  /** Keep this column in the 768-1023px table. Unsaid on every column = the first three. */
  tablet?: boolean;
}

export type ColumnsConfig = Record<string, ColumnConfig>;

/** Below which width the table becomes cards: phones only (< 768), or tablets too (< 1024). */
export type CardBelow = 'phone' | 'tablet';

/** The selector the card uses as its title: the one marked `title`, else the first column. */
export function titleSelectorOf(selectors: string[], columns?: ColumnsConfig): string | undefined {
  const marked = columns ? selectors.find((s) => columns[s]?.card === 'title') : undefined;
  return marked ?? selectors[0];
}

/**
 * The columns the tablet table keeps.
 *
 * The page's choice when it marked any column `tablet: true`; otherwise the first three, which is
 * what every page got before it could choose. That default is why the Jobs list lost its Status
 * column at 900px: Status was sixth in a list whose first three were title, date and company.
 */
export function tabletSelectorsOf(selectors: string[], columns?: ColumnsConfig): string[] {
  const chosen = columns ? selectors.filter((s) => columns[s]?.tablet) : [];
  if (chosen.length > 0) return chosen;
  return selectors.length > TABLET_COLUMNS ? selectors.slice(0, TABLET_COLUMNS) : selectors;
}

/**
 * The visible text of a rendered cell, or '' when there is none to read.
 *
 * Most list pages write their renderers as `(row) => <>{row.x || '-'}</>`, so
 * the text is a plain child of a fragment and this finds it. Some render a
 * component instead — `<CRMStatusChip status={...} />` keeps its text in a
 * prop, not in children — and for those there is nothing to walk.
 */
export function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement(node)) {
    const { children } = (node.props ?? {}) as { children?: React.ReactNode };
    return textOf(children);
  }
  return '';
}

/** Placeholders the renderers use for "nothing here". */
const PLACEHOLDER = new Set(['-', '—', 'N/A', 'null', 'undefined']);

/** True when a node renders a real component rather than only text. */
export function rendersComponent(node: React.ReactNode): boolean {
  if (Array.isArray(node)) return node.some(rendersComponent);
  if (!React.isValidElement(node)) return false;
  // A fragment is a wrapper, not a component; look through it.
  if (node.type === React.Fragment) {
    const { children } = (node.props ?? {}) as { children?: React.ReactNode };
    return rendersComponent(children);
  }
  return true;
}

/**
 * Whether a cell is worth a line on a card.
 *
 * Empty text is not enough on its own: a chip or an icon has no text child, so
 * treating "no readable text" as empty silently dropped every status column
 * that renders a component — which is most of them. So a node with no text
 * counts as present whenever it actually renders something.
 */
export const isEmptyValue = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || typeof node === 'boolean') return true;
  const text = textOf(node).trim();
  if (text) return PLACEHOLDER.has(text);
  return !rendersComponent(node);
};

/**
 * Columns that describe state rather than identity. Matched on the selector,
 * not the label, because labels get renamed and selectors are the API.
 */
const STATUS_SELECTOR = /(^|[a-z])(status|stage)$/i;

/**
 * Columns not worth a line of their own on a phone: a yes/no flag reads as
 * noise next to a location or a date. They stay in the facts list, just never
 * as the subtitle.
 */
const isBoolean = (node: React.ReactNode) => {
  const t = textOf(node).trim().toLowerCase();
  return t === 'yes' || t === 'no' || t === 'true' || t === 'false';
};

/**
 * Assign each column a part to play.
 *
 * `fields` must already be in the page's own column order, with the first
 * being the primary column — that is the order the table itself uses, so the
 * card and the table never disagree about what a row is called.
 */
export function buildCardLayout(
  title: React.ReactNode,
  fields: CardField[],
  columns?: ColumnsConfig,
): CardLayout {
  if (columns) return layoutFromConfig(title, fields, columns);

  const present = fields.filter((f) => !isEmptyValue(f.value));

  const statusIndex = present.findIndex((f) => STATUS_SELECTOR.test(f.selector));
  const status = statusIndex === -1 ? null : present[statusIndex];
  const rest = present.filter((_, i) => i !== statusIndex);

  // The subtitle is the first column that identifies the record — a company,
  // an owner, an email. A boolean flag or a date does not, so skip past them.
  const subtitleIndex = rest.findIndex((f) => !isBoolean(f.value));
  const subtitle = subtitleIndex === -1 ? null : rest[subtitleIndex];

  return {
    title,
    status,
    subtitle,
    facts: rest.filter((_, i) => i !== subtitleIndex),
    actions: [],
  };
}

/**
 * The page said which column plays which part, so do exactly that.
 *
 * A column with no `card` is a fact. A second `status` or `subtitle` falls back to a fact rather
 * than vanishing: a config mistake should cost layout, not data. Empty cells are still dropped,
 * actions included, so a row with nothing to do gets no empty footer.
 */
function layoutFromConfig(title: React.ReactNode, fields: CardField[], columns: ColumnsConfig): CardLayout {
  let status: CardField | null = null;
  let subtitle: CardField | null = null;
  const facts: CardField[] = [];
  const actions: CardField[] = [];

  for (const f of fields) {
    const role = columns[f.selector]?.card ?? 'fact';
    if (role === 'hidden' || role === 'title' || isEmptyValue(f.value)) continue;
    if (role === 'action') actions.push(f);
    else if (role === 'status' && !status) status = f;
    else if (role === 'subtitle' && !subtitle) subtitle = f;
    else facts.push(f);
  }

  return { title, status, subtitle, facts, actions };
}
